import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Trash2,
  FileText,
  Users,
  Leaf,
  Check,
  Search,
  Package,
  Copy,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  usePriceList,
  usePriceListArticles,
  usePriceLists,
  useArticles,
  useArticleCategories,
  useCreatePriceList,
  useUpdatePriceList,
  useDeletePriceList,
  useSetPriceListArticlePrices,
  useCopyPricesFrom,
  useCanDeletePriceList
} from '../../hooks/useSupabase'
import { priceCalculations, priceListsService } from '../../services/priceLists'
import type { Database } from '../../lib/supabase'

type PriceListInsert = Database['public']['Tables']['price_lists']['Insert']
type Article = Database['public']['Tables']['articles']['Row']

export function PriceListDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNewPriceList = id === 'new'

  // Stable empty arrays to prevent infinite loops
  const emptyPriceListArticles = useMemo(() => [] as Awaited<ReturnType<typeof priceListsService.getArticles>>, [])
  const emptyArticles = useMemo(() => [] as Article[], [])
  const emptyPriceLists = useMemo(() => [] as Awaited<ReturnType<typeof priceListsService.getAll>>, [])

  // Data fetching
  const { data: priceList, isLoading: loadingPriceList } = usePriceList(id || '')
  const { data: priceListArticlesData, isLoading: loadingArticles, refetch: refetchArticles } = usePriceListArticles(id || '')
  const { data: allArticlesData } = useArticles()
  const { data: allPriceListsData } = usePriceLists()
  const { data: categories = [] } = useArticleCategories()

  // Use stable references
  const priceListArticles = priceListArticlesData ?? emptyPriceListArticles
  const allArticles = allArticlesData ?? emptyArticles
  const allPriceLists = allPriceListsData ?? emptyPriceLists

  // Category helpers
  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Ingen kategori'
    const category = categories.find(c => c.id === categoryId)
    return category?.name || 'Okänd kategori'
  }

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#6B7280'
    const category = categories.find(c => c.id === categoryId)
    return category?.color || '#6B7280'
  }

  // Mutations
  const createPriceList = useCreatePriceList()
  const updatePriceList = useUpdatePriceList()
  const deletePriceList = useDeletePriceList()
  const saveArticlePricesMutation = useSetPriceListArticlePrices()
  const copyPricesFrom = useCopyPricesFrom()
  const { data: canDeleteData } = useCanDeletePriceList(id || '')

  // Form state
  const [formData, setFormData] = useState<PriceListInsert>({
    name: '',
    description: '',
    type: 'customer',
    is_default: false,
    is_active: true
  })

  // Article prices state - stores both ex and incl values
  const [articlePrices, setArticlePrices] = useState<Map<string, { exclVat: number; inclVat: number; lastEdited: 'excl' | 'incl' }>>(new Map())
  const [searchTerm, setSearchTerm] = useState('')
  const [saving, setSaving] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copying, setCopying] = useState(false)

  // Navigation between filtered price lists
  const [filteredPriceListIds, setFilteredPriceListIds] = useState<string[]>([])

  // Load filtered price list IDs from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('filtered-pricelist-ids')
      if (saved) {
        setFilteredPriceListIds(JSON.parse(saved))
      }
    } catch (e) {
      console.error('Error loading filtered price list IDs:', e)
    }
  }, [])

  // Calculate navigation state (with wrap-around)
  const currentIndex = id && !isNewPriceList ? filteredPriceListIds.indexOf(id) : -1
  const canNavigate = filteredPriceListIds.length > 1 && currentIndex >= 0
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : filteredPriceListIds.length - 1
  const nextIndex = currentIndex < filteredPriceListIds.length - 1 ? currentIndex + 1 : 0

  // Check for unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!priceList) return false
    return formData.name !== priceList.name ||
      formData.description !== (priceList.description || '') ||
      formData.type !== priceList.type ||
      formData.is_default !== priceList.is_default ||
      formData.is_active !== priceList.is_active
  }

  // Navigate to previous price list (with wrap-around)
  const navigateToPrevious = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/pricelists/${filteredPriceListIds[previousIndex]}`)
  }

  // Navigate to next price list (with wrap-around)
  const navigateToNext = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/pricelists/${filteredPriceListIds[nextIndex]}`)
  }

  // Keyboard navigation with Page Up/Down
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return
      }
      if (e.key === 'PageUp') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'PageDown') {
        e.preventDefault()
        navigateToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canNavigate, filteredPriceListIds, previousIndex, nextIndex])

  // Filter price lists for copy - same type, not current list, with prices
  const copyablePriceLists = useMemo(() => {
    if (!priceList) return []
    return allPriceLists.filter(pl =>
      pl.id !== id &&
      pl.type === priceList.type &&
      pl.article_count > 0
    )
  }, [allPriceLists, priceList, id])

  // Load price list data
  useEffect(() => {
    if (priceList) {
      setFormData({
        name: priceList.name,
        description: priceList.description || '',
        type: priceList.type,
        is_default: priceList.is_default,
        is_active: priceList.is_active
      })
    }
  }, [priceList])

  // Load article prices when data changes
  useEffect(() => {
    // Only run when we have articles loaded and price list articles data is ready
    if (allArticles.length === 0 || loadingArticles) return

    // Only update if we have price list articles
    if (priceListArticles.length === 0) return

    const pricesMap = new Map<string, { exclVat: number; inclVat: number; lastEdited: 'excl' | 'incl' }>()

    priceListArticles.forEach(pla => {
      const articleId = pla.article?.id || pla.article_id
      if (!articleId) return

      const vatRate = pla.article?.vat_rate ?? 25
      const priceExclVat = typeof pla.price_excl_vat === 'string'
        ? parseFloat(pla.price_excl_vat)
        : (pla.price_excl_vat || 0)

      const inclVat = priceCalculations.calculatePriceInclVat(priceExclVat, vatRate)
      pricesMap.set(articleId, { exclVat: priceExclVat, inclVat, lastEdited: 'excl' })
    })

    setArticlePrices(pricesMap)
  }, [priceListArticles, allArticles, loadingArticles])

  // Filter and sort articles
  const filteredArticles = useMemo(() => {
    let result = [...allArticles]

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      result = result.filter(article =>
        article.name.toLowerCase().includes(lowerSearch) ||
        article.description?.toLowerCase().includes(lowerSearch) ||
        getCategoryName(article.category).toLowerCase().includes(lowerSearch)
      )
    }

    // Sort by category name then article name
    result.sort((a, b) => {
      const categoryCompare = getCategoryName(a.category).localeCompare(getCategoryName(b.category), 'sv')
      if (categoryCompare !== 0) return categoryCompare
      return a.name.localeCompare(b.name, 'sv')
    })

    return result
  }, [allArticles, searchTerm])

  // Handle price change - ex moms
  const handlePriceExclChange = (articleId: string, value: string, vatRate: number) => {
    const numValue = parseFloat(value) || 0
    const inclVat = numValue > 0 ? priceCalculations.calculatePriceInclVat(numValue, vatRate) : 0
    setArticlePrices(prev => {
      const newMap = new Map(prev)
      newMap.set(articleId, { exclVat: numValue, inclVat, lastEdited: 'excl' })
      return newMap
    })
  }

  // Handle price change - ink moms
  const handlePriceInclChange = (articleId: string, value: string, vatRate: number) => {
    const numValue = parseFloat(value) || 0
    const exclVat = numValue > 0 ? priceCalculations.calculatePriceExclVat(numValue, vatRate) : 0
    setArticlePrices(prev => {
      const newMap = new Map(prev)
      newMap.set(articleId, { exclVat, inclVat: numValue, lastEdited: 'incl' })
      return newMap
    })
  }

  // Get article details for display
  const getArticleDetails = (article: Article) => {
    const priceData = articlePrices.get(article.id)
    const vatRate = article.vat_rate ?? 25
    const unit = article.unit ?? 'st'
    const priceExclVat = priceData?.exclVat || 0
    const priceInclVat = priceData?.inclVat || 0
    const vatAmount = priceExclVat > 0 ? priceCalculations.calculateVatAmount(priceExclVat, vatRate) : 0

    return {
      priceExclVat,
      priceInclVat,
      vatRate,
      vatAmount,
      unit
    }
  }

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (isNewPriceList) return true

    // Check form data changes
    if (priceList) {
      if (formData.name !== priceList.name) return true
      if (formData.description !== (priceList.description || '')) return true
      if (formData.type !== priceList.type) return true
      if (formData.is_default !== priceList.is_default) return true
      if (formData.is_active !== priceList.is_active) return true
    }

    // Check price changes
    const originalPrices = new Map<string, number>()
    priceListArticles.forEach(pla => {
      originalPrices.set(pla.article_id, pla.price_excl_vat)
    })

    for (const [articleId, priceData] of articlePrices) {
      const originalPrice = originalPrices.get(articleId) ?? 0
      if (priceData.exclVat !== originalPrice && priceData.exclVat >= 0) return true
    }

    return false
  }, [isNewPriceList, priceList, formData, priceListArticles, articlePrices])

  // Save handler
  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Namn är obligatoriskt')
      return
    }

    try {
      setSaving(true)

      let priceListId = id

      if (isNewPriceList) {
        // Create new price list
        const result = await createPriceList.mutateAsync(formData)
        priceListId = result.id
        toast.success('Prislista skapad')
      } else {
        // Update existing price list
        if (!id) return
        await updatePriceList.mutateAsync({ id, updates: formData })
        toast.success('Prislista uppdaterad')
      }

      // Save article prices (include zero prices - allows setting price to 0)
      const pricesToSave: { articleId: string; priceExclVat: number }[] = []
      articlePrices.forEach((priceData, articleId) => {
        if (priceData.exclVat > 0 || priceListArticles.some(pla => pla.article_id === articleId)) {
          pricesToSave.push({ articleId, priceExclVat: priceData.exclVat })
        }
      })

      if (pricesToSave.length > 0 && priceListId && priceListId !== 'new') {
        await saveArticlePricesMutation.mutateAsync({
          priceListId,
          prices: pricesToSave
        })
      }

      // Navigate to the created/updated price list
      if (isNewPriceList && priceListId) {
        navigate(`/admin/pricelists/${priceListId}`, { replace: true })
      }
    } catch (error) {
      console.error('Error saving price list:', error)
      toast.error('Kunde inte spara prislista')
    } finally {
      setSaving(false)
    }
  }

  // Delete handler
  const handleDelete = async () => {
    if (isNewPriceList || !id) return

    // Check if deletion is allowed
    if (!canDeleteData?.canDelete) {
      toast.error(canDeleteData?.reason || 'Kan inte ta bort denna prislista')
      return
    }

    if (!confirm('Är du säker på att du vill ta bort denna prislista?')) {
      return
    }

    try {
      await deletePriceList.mutateAsync(id)
      toast.success('Prislista borttagen')
      navigate('/admin/pricelists')
    } catch (error) {
      console.error('Error deleting price list:', error)
      toast.error('Kunde inte ta bort prislista')
    }
  }

  // Copy prices from another list handler
  const handleCopyFrom = async (fromPriceListId: string) => {
    if (isNewPriceList || !id) {
      toast.error('Spara prislistan först innan du kopierar priser')
      return
    }

    const sourcePriceList = allPriceLists.find(pl => pl.id === fromPriceListId)
    if (!sourcePriceList) {
      toast.error('Källprislistan hittades inte')
      return
    }

    if (!confirm(`Vill du kopiera alla priser från "${sourcePriceList.name}"? Befintliga priser kommer att skrivas över.`)) {
      return
    }

    setCopying(true)
    try {
      await copyPricesFrom.mutateAsync({ fromPriceListId, toPriceListId: id })
      // Refetch to update local state
      await refetchArticles()
      toast.success(`${sourcePriceList.article_count} priser kopierade från "${sourcePriceList.name}"`)
      setShowCopyModal(false)
    } catch (error: any) {
      console.error('Error copying prices:', error)
      toast.error(error?.message || 'Kunde inte kopiera priser')
    } finally {
      setCopying(false)
    }
  }

  // Loading state
  if (!isNewPriceList && (loadingPriceList || loadingArticles)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar prislista...</p>
        </div>
      </div>
    )
  }

  // Not found state
  if (!isNewPriceList && !loadingPriceList && !priceList) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-nature-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
          Prislista hittades inte
        </h3>
        <button
          onClick={() => navigate('/admin/pricelists')}
          className="btn-primary mt-4"
        >
          Tillbaka till prislistor
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/pricelists')}
            className="p-2 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-nature-600 dark:text-nature-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
              {isNewPriceList ? 'Ny prislista' : priceList?.name}
            </h1>
            <p className="text-nature-600 dark:text-nature-400">
              {isNewPriceList ? 'Skapa ny prislista' : 'Redigera prislista och priser'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Navigation between filtered price lists (with wrap-around) */}
          {!isNewPriceList && filteredPriceListIds.length > 1 && (
            <div className="flex items-center gap-1 border-r border-nature-200 dark:border-nature-600 pr-3 mr-1">
              <button
                onClick={navigateToPrevious}
                className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                title="Föregående prislista (Page Up)"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-xs text-nature-500 dark:text-nature-400 min-w-[3rem] text-center">
                {currentIndex + 1} / {filteredPriceListIds.length}
              </span>
              <button
                onClick={navigateToNext}
                className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                title="Nästa prislista (Page Down)"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
          {!isNewPriceList && (
            <>
              <div className="relative">
                <button
                  onClick={() => setShowCopyModal(!showCopyModal)}
                  disabled={copyablePriceLists.length === 0 || copying}
                  className={`flex items-center space-x-2 font-medium py-2 px-4 rounded-lg transition-colors ${
                    copyablePriceLists.length > 0 && !copying
                      ? 'bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-700 dark:text-nature-300'
                      : 'bg-nature-100 dark:bg-nature-800 text-nature-400 cursor-not-allowed'
                  }`}
                  title={copyablePriceLists.length > 0 ? 'Kopiera priser från annan lista' : 'Inga andra prislistor att kopiera från'}
                >
                  <Copy className={`h-4 w-4 ${copying ? 'animate-pulse' : ''}`} />
                  <span>{copying ? 'Kopierar...' : 'Kopiera från'}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showCopyModal && !copying && (
                  <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 z-50">
                    <div className="p-3 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
                      <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                        Kopiera priser från
                      </span>
                      <button
                        onClick={() => setShowCopyModal(false)}
                        className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded"
                      >
                        <X className="h-4 w-4 text-nature-500" />
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {copyablePriceLists.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-nature-500 dark:text-nature-400">
                          Inga andra prislistor av samma typ med priser
                        </div>
                      ) : (
                        copyablePriceLists.map(pl => (
                          <button
                            key={pl.id}
                            onClick={() => handleCopyFrom(pl.id)}
                            className="w-full px-4 py-3 text-left hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors flex items-center justify-between"
                          >
                            <div>
                              <div className="text-sm font-medium text-nature-900 dark:text-nature-100 flex items-center gap-2">
                                {pl.name}
                                {pl.is_default && <Check className="h-3 w-3 text-green-600" />}
                              </div>
                              <div className="text-xs text-nature-500 dark:text-nature-400">
                                {pl.article_count} artiklar med pris
                              </div>
                            </div>
                            <Copy className="h-4 w-4 text-nature-400" />
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleDelete}
                disabled={!canDeleteData?.canDelete}
                className={`flex items-center space-x-2 font-medium py-2 px-4 rounded-lg transition-colors ${
                  canDeleteData?.canDelete
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-red-200 dark:bg-red-900/30 text-red-400 dark:text-red-600 cursor-not-allowed'
                }`}
                title={canDeleteData?.canDelete ? 'Ta bort prislista' : canDeleteData?.reason}
              >
                <Trash2 className="h-4 w-4" />
                <span>Ta bort</span>
              </button>
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Sparar...' : 'Spara'}</span>
          </button>
        </div>
      </div>

      {/* Price List Info Card */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-6">
        <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">
          Grunduppgifter
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Namn *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-field"
              placeholder="Prislistans namn"
            />
          </div>

          <div>
            <label className="form-label">Typ *</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'customer' })}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                  formData.type === 'customer'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500'
                }`}
              >
                <Users className="h-5 w-5" />
                <span className="font-medium">Kund</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'subcontractor' })}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                  formData.type === 'subcontractor'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'border-nature-200 dark:border-nature-600 hover:border-nature-300 dark:hover:border-nature-500'
                }`}
              >
                <Leaf className="h-5 w-5" />
                <span className="font-medium">Trädgårdsmästare</span>
              </button>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="form-label">Beskrivning</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field"
              rows={2}
              placeholder="Valfri beskrivning"
            />
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-nature-300 rounded"
              />
              <Check className={`h-4 w-4 ${formData.is_default ? 'text-green-600' : 'text-nature-400'}`} />
              <span className="text-sm font-medium text-nature-900 dark:text-nature-100">
                Standardprislista
              </span>
            </label>

            <div>
              <label className="form-label">Status</label>
              <label className="flex items-center gap-3 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-nature-300 text-primary-600 focus:ring-primary-500 dark:border-nature-600 dark:bg-nature-700"
                />
                <span className={`text-sm font-medium ${formData.is_active ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formData.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Articles Pricing Section */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <div className="p-6 border-b border-nature-200 dark:border-nature-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Artikelpriser
              </h2>
              <p className="text-sm text-nature-600 dark:text-nature-400">
                Ange pris ex moms eller ink moms - det andra beräknas automatiskt.
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-nature-400" />
              <input
                type="text"
                placeholder="Sök artiklar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-nature-50 dark:bg-nature-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Artikel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Moms
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Pris ex moms
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Moms
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase tracking-wider">
                  Pris ink moms
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
              {filteredArticles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Package className="h-12 w-12 text-nature-400 mx-auto mb-4" />
                    <p className="text-nature-600 dark:text-nature-400">
                      {searchTerm ? 'Inga artiklar matchade sökningen' : 'Inga artiklar finns'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredArticles.map((article) => {
                  const details = getArticleDetails(article)
                  return (
                    <tr
                      key={article.id}
                      className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-nature-900 dark:text-nature-100">
                            {article.name}
                          </div>
                          {article.description && (
                            <div className="text-xs text-nature-500 dark:text-nature-400 truncate max-w-xs">
                              {article.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-2 py-0.5 text-xs rounded-full text-white font-medium"
                          style={{ backgroundColor: getCategoryColor(article.category) }}
                        >
                          {getCategoryName(article.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex px-2 py-1 text-xs rounded-full bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 font-medium">
                          {details.vatRate}%
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <input
                            type="number"
                            value={details.priceExclVat || ''}
                            onChange={(e) => handlePriceExclChange(article.id, e.target.value, details.vatRate)}
                            className="w-24 text-right input-field py-1 px-2 text-sm"
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                          <span className="text-xs text-nature-500 dark:text-nature-400 w-12">
                            kr
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-nature-600 dark:text-nature-400">
                          {details.priceExclVat > 0
                            ? priceCalculations.formatPrice(details.vatAmount)
                            : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <input
                            type="number"
                            value={details.priceInclVat ? Math.round(details.priceInclVat * 100) / 100 : ''}
                            onChange={(e) => handlePriceInclChange(article.id, e.target.value, details.vatRate)}
                            className="w-24 text-right input-field py-1 px-2 text-sm"
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                          <span className="text-xs text-nature-500 dark:text-nature-400 w-12">
                            kr/{details.unit}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        {!isNewPriceList && (
          <div className="p-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-700/30">
            <div className="flex items-center justify-between text-sm">
              <span className="text-nature-600 dark:text-nature-400">
                Totalt {allArticles.length} artiklar, {articlePrices.size} med pris
              </span>
              <span className="text-nature-600 dark:text-nature-400">
                Priser som sparas: {Array.from(articlePrices.values()).filter(p => p.exclVat > 0).length} st
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/pricelists')}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Tillbaka</span>
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          <span>{saving ? 'Sparar...' : 'Spara ändringar'}</span>
        </button>
      </div>
    </div>
  )
}
